import type { FC } from "react";
import { useEffect, useRef, useState } from "react";

import { SimpleChipItem, Textfield } from "@similarweb/ui-components";
import { Stack, Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";

import { LLMSetupSectionHeader } from "./LLMSetupSectionHeader";

const MAX_VISIBLE_ROWS = 3;

interface ILLMSetupBrandInputProps {
    brandNames: string[];
    onBrandsChange: (brands: string[]) => void;
    error?: string;
}

export const LLMSetupBrandInput: FC<ILLMSetupBrandInputProps> = ({
    brandNames = [],
    onBrandsChange,
    error: externalError,
}) => {
    const [selectedBrands, setSelectedBrands] = useState<string[]>(brandNames);
    const selectedBrandsRef = useRef<string[]>(selectedBrands);
    const [brandToAdd, setBrandToAdd] = useState<string>("");
    const [internalError, setInternalError] = useState<string>("");
    const topicFieldRef = useRef<Textfield>(null);

    const error = internalError || externalError || "";

    const onApplyClick = (): void => {
        if (!brandToAdd) {
            return;
        }

        setSelectedBrands([...selectedBrands, brandToAdd]);
        setBrandToAdd("");
    };

    const onBrandToAddChange = (value: string): void => {
        const trimmedValue = value.trim();
        const isDuplicate = selectedBrandsRef.current.some((b) => b.toLowerCase() === trimmedValue.toLowerCase());

        if (isDuplicate) {
            setInternalError(translate("llmTracker.campaignQuery.editBrand.error.duplicate"));
            return;
        }

        setInternalError("");
        setBrandToAdd(trimmedValue);
    };

    const onFieldKeyDown = (ev: KeyboardEvent): void => {
        if (ev.key === "Enter") {
            onApplyClick();
            topicFieldRef?.current?.clearValue?.();
        }
    };

    useEffect(() => {
        onBrandsChange?.(selectedBrands);
        selectedBrandsRef.current = selectedBrands;
    }, [selectedBrands]);

    //treat first typed brand as an actual brand in the list, without forcing the user to click "Add".
    //gate only on internalError (e.g. duplicates) — not externalError, otherwise the user gets stuck:
    //externalError keeps brandNames empty -> validation keeps externalError truthy -> cannot recover by typing.
    useEffect(() => {
        if (!selectedBrands.length) {
            const virtualBrandsList = brandToAdd && !internalError ? [brandToAdd] : [];
            onBrandsChange?.(virtualBrandsList);
        }
    }, [selectedBrands, brandToAdd, internalError]);

    return (
        <Stack gap="12px">
            <LLMSetupSectionHeader
                title={
                    <Text
                        fontVariant="subtitle2"
                        data-automation="llm-setup-brand-label"
                    >
                        {translate("llm.setup.modal.brand.label")}
                    </Text>
                }
            />
            <Textfield
                ref={topicFieldRef}
                dataAutomation="llm-setup-brand-input"
                placeholder={translate("llm.setup.modal.brand.placeholder")}
                ApplyButtonText={translate("global.add")}
                onChange={onBrandToAddChange}
                onApplyClick={internalError ? undefined : onApplyClick}
                error={!!error}
                errorMessage={error}
                onKeyDown={onFieldKeyDown}
            />
            <Flex
                flexWrap="wrap"
                overflow="auto"
                maxHeight={`${40 * MAX_VISIBLE_ROWS}px`}
            >
                {selectedBrands.map((brand) => (
                    <SimpleChipItem
                        key={brand}
                        text={brand}
                        onCloseItem={() => setSelectedBrands(selectedBrands.filter((b) => b !== brand))}
                    />
                ))}
            </Flex>
        </Stack>
    );
};
